import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { List, useDynamicRowHeight, useListRef, type RowComponentProps } from 'react-window';
import { MoldOrder } from '../../types/mold';
import MoldOrderCard from './MoldOrderCard';
import useMediaQuery from '../../hooks/useMediaQuery';
import { sortByPriority } from '../../utils/priority';

interface MoldOrderCardViewProps {
  moldOrders: MoldOrder[];
  // Server-paged mode: the list is already just the current page (≤ pageSize), so below lg we
  // drop the fixed-height virtualized scroller and render a plain grid that flows with the page
  // (normal browser scroll). Client mode (a potentially huge unpaginated list) always virtualizes.
  pageScroll?: boolean;
}

type RowProps = { rows: MoldOrder[][]; columns: number };

// No fixed height on the row: useDynamicRowHeight measures the inner grid's natural height.
function CardRow({ index, style, rows, columns }: RowComponentProps<RowProps>) {
  const items = rows[index];
  return (
    <div style={style}>
      <div className={`grid ${columns === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-4 pb-4`}>
        {items.map((order) => (
          <MoldOrderCard key={order.id} moldOrder={order} />
        ))}
      </div>
    </div>
  );
}

const MoldOrderCardView: React.FC<MoldOrderCardViewProps> = ({
  moldOrders,
  pageScroll = false,
}) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const columns = useMediaQuery('(min-width: 768px)') ? 2 : 1;

  const sortedMoldOrders = useMemo(
    () => sortByPriority(moldOrders),
    [moldOrders],
  );

  // Group into rows of `columns` orders so the responsive 2-col grid maps onto virtualized rows.
  const rows = useMemo(() => {
    const chunked: MoldOrder[][] = [];
    for (let i = 0; i < sortedMoldOrders.length; i += columns) {
      chunked.push(sortedMoldOrders.slice(i, i + columns));
    }
    return chunked;
  }, [sortedMoldOrders, columns]);

  const listRef = useListRef(null);

  // resetKey clears the measured-height cache when the column count or dataset changes.
  // Keyed on count + first/last id so search/filter changes are caught; in-place edits
  // that preserve all three self-correct on re-measure as rows scroll into view.
  const resetKey = `${columns}:${sortedMoldOrders.length}:${sortedMoldOrders[0]?.id ?? 0}:${sortedMoldOrders[sortedMoldOrders.length - 1]?.id ?? 0}`;
  // ~279px matches the real row height so the scrollbar thumb stays stable until rows are measured.
  const rowHeight = useDynamicRowHeight({ defaultRowHeight: 279, key: resetKey });

  // Scroll back to the top when the filtered list changes (e.g. a new search).
  useEffect(() => {
    listRef.current?.scrollToRow({ index: 0, align: 'start' });
  }, [resetKey, listRef]);

  // Size the List from its top to the viewport bottom (robust vs. the ancestor flex chain).
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(0);
  useLayoutEffect(() => {
    const compute = () => {
      const top = wrapperRef.current?.getBoundingClientRect().top ?? 0;
      setListHeight(Math.max(240, window.innerHeight - top - 16));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
    // Recompute when switching to the virtualized List (isDesktop flips true after mount, at
    // which point wrapperRef is finally attached): otherwise listHeight keeps its plain-grid
    // mount value (top=0) and the List is mis-sized on desktop.
  }, [isDesktop]);

  if (sortedMoldOrders.length === 0) {
    return (
      <div className="text-center text-gray-500 py-10">
        <div className="flex flex-col items-center">
          <svg className="w-12 h-12 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-3-3v6m-7 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
          <span>ไม่มีข้อมูลที่จะแสดง</span>
        </div>
      </div>
    );
  }

  // Below lg in server-paged mode: plain responsive grid, no fixed-height inner scroller. The
  // cards extend down the page and the browser's normal scroll takes over (only the current
  // page's rows render, so virtualization isn't needed).
  if (pageScroll && !isDesktop) {
    return (
      <div className={`grid ${columns === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-4 pb-4`}>
        {sortedMoldOrders.map((order) => (
          <MoldOrderCard key={order.id} moldOrder={order} />
        ))}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="w-full">
      <List
        listRef={listRef}
        rowComponent={CardRow}
        rowCount={rows.length}
        rowHeight={rowHeight}
        rowProps={{ rows, columns }}
        overscanCount={4}
        style={{ height: listHeight }}
      />
    </div>
  );
};

export default MoldOrderCardView;
